import type { QueryClient } from "@tanstack/react-query";
import { invalidateMachines } from "@/hooks/use-machines";
import {
  type BatchAcquireResult,
  finishAcquireError,
  finishAcquireSuccess,
  finishBatchAcquire,
  startAcquireToast,
} from "@/lib/acquire-toast";
import { api } from "@/lib/api";
import type { ClipboardMachine } from "@/lib/machine-clipboard";
import { machineHost } from "@/lib/machine-host";
import type { Machine } from "@/lib/types";

/** Fire a single-machine acquire + its toast. No confirmation, no reboot. */
export function runSingleAcquire(machine: Machine, queryClient: QueryClient): void {
  // Bare promise, not a react-query observer: the row unmounts before the
  // response lands, tearing down an observer's toast callbacks.
  const acquireToast = startAcquireToast(machine.name, [
    { name: machine.name, ip: machineHost(machine) },
  ]);
  api
    .acquireMachine(machine.id)
    .then((data) => {
      acquireToast.stop();
      finishAcquireSuccess(acquireToast.id, data);
    })
    .catch((error) => {
      acquireToast.stop();
      finishAcquireError(
        acquireToast.id,
        machine.name,
        error instanceof Error ? error.message : String(error),
      );
    })
    .finally(() => invalidateMachines(queryClient));
}

/** Web-only takeover of another user's disposable lease. */
export function runLooseAcquire(machine: Machine, queryClient: QueryClient): void {
  const acquireToast = startAcquireToast(machine.name, [
    { name: machine.name, ip: machineHost(machine) },
  ]);
  api
    .looseAcquireMachine(machine.id)
    .then((data) => {
      acquireToast.stop();
      finishAcquireSuccess(acquireToast.id, data);
    })
    .catch((error) => {
      acquireToast.stop();
      finishAcquireError(
        acquireToast.id,
        machine.name,
        error instanceof Error ? error.message : String(error),
      );
    })
    .finally(() => invalidateMachines(queryClient));
}

/** Batch acquire + one consolidated toast. A batch of one delegates to the single path. */
export function runBatchAcquire(machines: Machine[], queryClient: QueryClient): void {
  const [firstMachine] = machines;
  if (!firstMachine) return;
  if (machines.length === 1) {
    runSingleAcquire(firstMachine, queryClient);
    return;
  }
  // Bare promise so the toast survives the caller unmounting.
  const copyTargets: ClipboardMachine[] = machines.map((machine) => ({
    name: machine.name,
    ip: machineHost(machine),
  }));
  const namesById = new Map(machines.map((machine) => [machine.id, machine.name]));
  const acquireToast = startAcquireToast(`${machines.length} machines`, copyTargets);
  api
    .acquireMachinesBatch(machines.map((machine) => machine.id))
    .then((data) => {
      acquireToast.stop();
      const results: BatchAcquireResult[] = data.results.map((result) => ({
        ok: result.ok,
        machine: result.machine,
        error: result.error,
        name: result.machine?.name ?? namesById.get(result.id) ?? `id=${result.id}`,
      }));
      finishBatchAcquire(acquireToast.id, results);
    })
    .catch((error) => {
      acquireToast.stop();
      finishAcquireError(
        acquireToast.id,
        `${machines.length} machines`,
        error instanceof Error ? error.message : String(error),
      );
    })
    .finally(() => invalidateMachines(queryClient));
}
